import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Alert, ListItem } from '@material-library';
import { ProvisionService } from 'src/app/account/services/provision.service';
import { SupportTicketService } from 'src/app/account/services/support-ticket.service';
import { LoggerService } from 'src/app/core/logger.service';
import { UserService } from 'src/app/core/user.service';
import { ErrCode, BlendEvent, User, PermissionActions } from 'src/app/shared/models';
import { BlendService } from 'src/app/shared/services/blend.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Base64Service } from 'src/app/core/base64.service';
import { requiredWithoutWhitespaces } from 'src/app/shared/validators';

export interface Fields {
  subject: string;
  description: string;
  attachLogs: boolean;
  name: string;
  email: string;
}

type FormControls<T> = {
  [K in keyof T]: FormControl<T[K] | null>;
};

type Field = keyof Fields;

@Component({
  selector: 'app-help-dialog',
  templateUrl: './help-dialog.component.html',
  styleUrls: ['./help-dialog.component.scss']
})
export class HelpDialogComponent implements OnInit, OnDestroy,OnChanges {

  @Input() openHelpDialog: boolean = false;
  @Output() openHelpDialogChange = new EventEmitter<boolean>();

  public viewContactSupport: boolean = false;
  public showThankYouTemplate: boolean = false; // Flag to show thank you template
  public helpCategories: ListItem[] = [];
  public email: string = '';
  public displayName: string;
  public descriptionError: string = '';
  public subjectError: string = '';
  private descriptionLength: number = 300;
  public errcode: ErrCode;
  public frmValues?: { [k: string]: any };
  public user: User | any;
  public isSupportSent: boolean = false; // Flag to track successful send
  public alertsConfig: { [key: string]: Alert } = {};
  public alertType: string = "thankYouBanner";
  public supportForm: FormGroup<FormControls<Fields>>;
  public submitted = false;
  @Input() public onlyOpenContactSupportDialog:boolean = false

  constructor(
    private loggerService: LoggerService,
    private userService: UserService,
    public blendService: BlendService,
    public provisionService: ProvisionService,
    private supportTicketService: SupportTicketService,
    private fb: FormBuilder,
    private base64: Base64Service,
  ) { }
  ngOnChanges(changes: SimpleChanges): void {
    if(changes['openHelpDialog'] && changes['openHelpDialog']['currentValue'] && this.onlyOpenContactSupportDialog){
      this.onContactSupportClick()
    }
  }

  ngOnInit(): void {
    this.user = this.userService.getUser();
    this.initHelpCategories();
    this.initForm();
    this.initAlerts();
  }

  initAlerts(): void {
    this.alertsConfig.thankYouBanner = {
      type: 'promo',
      showLeadIcon: true,
      leadIcon: 'info',
      title: `Thank you for your message.`,
      description: `Your support request has been successfully sent.`,
      showActions: true,
      showCloseButton: false,
      showSecondaryAction: false,
      secondaryButtonConfig: {
        label: ''
      },
      showPrimaryAction: false,
      primaryButtonConfig: {
        label: '',
        onClick: () => { }
      },
    };
  }

  initHelpCategories = () => {
    const isAdmin = this.userService.checkPermission(PermissionActions.MANAGE_BILLING);
    this.helpCategories = [
      {
        text: 'Web Panel Beta overview',
        url: 'https://help.sync.com/hc/en-us/articles/38447296012435-How-to-use-the-Sync-com-web-panel',
      },
      {
        text: 'Sharing files and folders with links',
        url: 'https://help.sync.com/hc/en-us/articles/38462804430355-Sharing-files-and-folders-with-links',
      },
      {
        text: 'Account and billing FAQ',
        url: 'https://help.sync.com/hc/en-us/articles/38507553585811-Account-and-billing-overview',
      },
      isAdmin && this.user.plan_sku?.startsWith('BPTSP') && {
        text: 'Pro Teams Standard getting started guide',
        url: 'https://help.sync.com/hc/en-us/articles/38508345510803-Getting-Started-With-Pro-Teams-Standard-Administrator-Guide',
      },
      isAdmin && this.user.plan_sku?.startsWith('BPTUP') && {
        text: 'Pro Teams+ Unlimited getting started guide ',
        url: 'https://help.sync.com/hc/en-us/articles/38508319327251-Getting-Started-With-Pro-Teams-Unlimited-Administrator-Guide',
      }
    ].filter(Boolean).map((item) => {
      return {
        primaryText: item.text,
        divider: true,
        isInteractive: true,
        onClick: () => window.open(item.url),
        trail2AdornmentConfig: {
          enabled: true,
          type: 'icon',
          size: 'large',
          iconType: 'primary',
          iconName: 'chevron-right',
        },
      };
    });
  }

  closeHelpDialog = () => {
    this.openHelpDialog = false;
    this.submitted = false;
    this.openHelpDialogChange.emit(this.openHelpDialog);
  }

  onContactSupportClick = () => {
    this.closeHelpDialog();
    this.viewContactSupport = true;
    this.blendService.track(BlendEvent.BUTTON_CLICKED, {button_text: 'Contact support'})
  }

  cleanupSupportState = () => {
    this.resetForm();
    this.viewContactSupport = false;
  }
  
  onCancelSupportClick = () => {
    this.cleanupSupportState();
    this.blendService.track(BlendEvent.CANCEL_HELP);
  }

  initForm(): void {
    this.supportForm = this.fb.group({
      subject: ['', [Validators.required, requiredWithoutWhitespaces]],
      description: ['', [Validators.required, requiredWithoutWhitespaces]],
      attachLogs: [true],
      name: [
        this.base64.decode(this.user?.display_name || '') || '',
        Validators.required,
      ],
      email: [this.user?.email || '', Validators.required],
    });
  }

  resetForm = () => {
    this.initForm();
    this.frmValues = {
      requester: this.user?.email ?? null,
      name: this.user?.first_name ?? null,
      subject: '',
      description: '',
      attachLogs: true
    };
  }

  handleClose = () => {
    this.resetForm();
    this.isSupportSent = false;
    this.viewContactSupport = false;
    this.showThankYouTemplate = false; // Reset the thank you template flag
  }

  getFieldError(fieldName: Field): string {
    const control = this.supportForm?.get(fieldName);
    if (!control?.touched) return '';

    if (control.errors?.required) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} cannot be empty.`;
    }
    if (control.errors?.maxlength) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} cannot exceed ${control.errors.maxlength.requiredLength} characters.`;
    }
    return '';
  }

  public sendSupportTicket = async (): Promise<void> => {
    this.submitted = true;
    if (this.supportForm.invalid) {
      Object.keys(this.supportForm.controls).forEach(key => {
        const control = this.supportForm.get(key);
        if (control.invalid) {
          control.markAsTouched();
        }
      });
      return;
    }

    try {
      const formValue = this.supportForm.value;
      const logs = formValue.attachLogs
        ? this.loggerService.dumpLogsText('all')
        : 'Not provided\n';
      const description = [
        'USER ID: ',
        this.user.uid,
        '--------',
        'Customer wrote:',
        formValue.description,
        '--------',
        'Javascript logging:',
        logs,
        '--------',
      ].join('\n\n');
      const postData = {
        z_requester: this.user.email,
        z_name: this.user.first_name,
        z_subject: formValue.subject,
        z_description: description,
        cpversion: '4.0'
      };
      
      this.isSupportSent = true;
      await this.supportTicketService.sendSupportTicket(postData);
      this.blendService.track(BlendEvent.CONTACT_SUPPORT, { 'logs': formValue.attachLogs });
    } catch (error) {
      throw new Error("Network error");
    }
  };

  public mpSyncKnowledgebase() {
    this.blendService.track(BlendEvent.SYNC_KNOWLEDGEBASE);
  }
  ngOnDestroy(){
    this.closeHelpDialog()
    this.cleanupSupportState()
  }
  // Can be used for future implementation for handling thank you modal in new UI
  // onSend = async () => {
  //   this.descriptionError = this.validLength(this.description, this.descriptionLength, 'description');
  //   this.subjectError = this.validSubject(this.subject);

  //   if (!this.descriptionError && !this.subjectError) {
  //     try {
  //       await this.sendSupportTicket();
  //       this.isSupportSent = true; // Set flag to true on successful send
  //     } catch (error) {
  //       this.snackbarService.showMessage(
  //         'critical',
  //         "Couldn't complete action due to a network error. Try again."
  //       );
  //     }
  //   }
  // }
}
